import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { INR, fmtDate, todayIso } from "@/lib/format";
import KPICard from "@/components/KPICard";
import { Plus, AlertTriangle, Search, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const METHODS = ["Bank Transfer", "UPI", "Cash", "Cheque", "Payment Gateway", "Other"];

export default function Finance() {
  const [params, setParams] = useSearchParams();
  const [payments, setPayments] = useState([]);
  const [outstanding, setOutstanding] = useState([]);
  const [dash, setDash] = useState(null);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(params.get("new") === "1");
  const [form, setForm] = useState({ client_id: "", amount: 0, payment_date: todayIso(), method: "Bank Transfer", reference: "", invoice_number: "", notes: "" });
  const nav = useNavigate();
  // Filters
  const [outClient, setOutClient] = useState("");
  const [outOverdue, setOutOverdue] = useState(params.get("filter") === "overdue" ? "gt0" : "");
  const [outSearch, setOutSearch] = useState("");
  const [payClient, setPayClient] = useState("");
  const [payMethod, setPayMethod] = useState("");
  const [payFrom, setPayFrom] = useState("");
  const [payTo, setPayTo] = useState("");

  const load = async () => {
    const [p, o, d, c] = await Promise.all([
      api.get("/payments"), api.get("/finance/outstanding"), api.get("/dashboard"), api.get("/clients"),
    ]);
    setPayments(p.data); setOutstanding(o.data); setDash(d.data); setClients(c.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.client_id || form.amount <= 0) { toast.error("Client and positive amount required"); return; }
    try {
      await api.post("/payments", form);
      toast.success("Payment recorded");
      setOpen(false); setParams({});
      setForm({ client_id: "", amount: 0, payment_date: todayIso(), method: "Bank Transfer", reference: "", invoice_number: "", notes: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filteredOutstanding = useMemo(() => {
    return outstanding.filter((o) => {
      if (outClient && o.client_id !== outClient) return false;
      if (outOverdue === "gt0" && !(o.days_overdue > 0)) return false;
      if (outOverdue === "gt30" && !(o.days_overdue > 30)) return false;
      if (outOverdue === "gt60" && !(o.days_overdue > 60)) return false;
      if (outSearch && !o.company_name.toLowerCase().includes(outSearch.toLowerCase())) return false;
      return true;
    });
  }, [outstanding, outClient, outOverdue, outSearch]);

  const filteredPayments = useMemo(() => {
    return payments.filter((p) => {
      if (payClient && p.client_id !== payClient) return false;
      if (payMethod && p.method !== payMethod) return false;
      if (payFrom && (p.payment_date || "") < payFrom) return false;
      if (payTo && (p.payment_date || "") > payTo) return false;
      return true;
    });
  }, [payments, payClient, payMethod, payFrom, payTo]);

  const METHODS_LIST = ["Bank Transfer", "UPI", "Cash", "Cheque", "Payment Gateway", "Other"];

  return (
    <div className="space-y-6" data-testid="finance-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Cash & Collections</div>
          <h1 className="text-4xl font-black tracking-tight">Finance</h1>
          <p className="text-sm text-muted-foreground mt-1">Track what's been billed, received, and what's still owed.</p>
        </div>
        <Sheet open={open} onOpenChange={(v) => { setOpen(v); if (!v) setParams({}); }}>
          <SheetTrigger asChild><Button data-testid="record-payment-btn" className="bg-primary hover:bg-primary/90"><Plus size={14} className="mr-1" />Record Payment</Button></SheetTrigger>
          <SheetContent className="sm:max-w-md overflow-y-auto">
            <SheetHeader><SheetTitle>Record Payment</SheetTitle></SheetHeader>
            <div className="mt-5 space-y-3">
              <div><Label className="font-mono text-[10px] uppercase">Client *</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                  <SelectTrigger data-testid="pay-form-client"><SelectValue placeholder="Choose client" /></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="font-mono text-[10px] uppercase">Amount (₹) *</Label><Input data-testid="pay-form-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value || 0) })} /></div>
                <div><Label className="font-mono text-[10px] uppercase">Date *</Label><Input type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} /></div>
              </div>
              <div><Label className="font-mono text-[10px] uppercase">Method</Label>
                <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{METHODS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="font-mono text-[10px] uppercase">Invoice #</Label><Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} /></div>
              <div><Label className="font-mono text-[10px] uppercase">Reference</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} /></div>
              <div><Label className="font-mono text-[10px] uppercase">Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <Button data-testid="pay-form-submit" onClick={save} className="w-full bg-primary hover:bg-primary/90">Save Payment</Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {dash?.kpis && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KPICard label="Contract Value" value={INR(dash.kpis.contract_value)} />
          <KPICard label="Received" value={INR(dash.kpis.received)} />
          <KPICard label="Outstanding" value={INR(dash.kpis.outstanding)} accent={dash.kpis.outstanding > 0} />
          <KPICard label="Overdue" value={INR(dash.kpis.overdue)} accent={dash.kpis.overdue > 0} />
        </div>
      )}

      <Tabs value={params.get("tab") || "outstanding"} onValueChange={(v) => { const p = new URLSearchParams(params); p.set("tab", v); setParams(p); }}>
        <TabsList>
          <TabsTrigger data-testid="tab-outstanding" value="outstanding">Outstanding ({outstanding.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-payments" value="payments">Payments ({payments.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="outstanding" className="mt-4">
          {params.get("filter") === "overdue" && (
            <div className="mb-3 flex items-center gap-2">
              <span data-testid="finance-overdue-chip" className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-mono text-[10px] uppercase tracking-widest bg-primary/15 text-primary">
                Overdue only
                <button
                  data-testid="finance-overdue-clear"
                  onClick={() => { const p = new URLSearchParams(params); p.delete("filter"); setParams(p); setOutOverdue(""); }}
                  className="ml-1 hover:text-foreground"
                >×</button>
              </span>
            </div>
          )}

          <div className="flex flex-wrap gap-2 mb-3 items-center">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="out-search" placeholder="Search client…" value={outSearch} onChange={(e) => setOutSearch(e.target.value)} className="pl-9 w-52" />
            </div>
            <Select value={outClient || "__all__"} onValueChange={(v) => setOutClient(v === "__all__" ? "" : v)}>
              <SelectTrigger className="w-52"><SelectValue placeholder="All clients" /></SelectTrigger>
              <SelectContent><SelectItem value="__all__">All clients</SelectItem>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={outOverdue || "__all__"} onValueChange={(v) => setOutOverdue(v === "__all__" ? "" : v)}>
              <SelectTrigger data-testid="out-overdue-filter" className="w-44"><SelectValue placeholder="Any age" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Any age</SelectItem>
                <SelectItem value="gt0">Overdue &gt; 0 days</SelectItem>
                <SelectItem value="gt30">Overdue &gt; 30 days</SelectItem>
                <SelectItem value="gt60">Overdue &gt; 60 days</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" onClick={load} title="Refresh"><RefreshCw size={14} /></Button>
          </div>
          <div className="border border-border rounded-md overflow-hidden bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  <th className="px-3 py-2.5">Client</th>
                  <th className="px-3 py-2.5 text-right">Contract</th>
                  <th className="px-3 py-2.5 text-right">Received</th>
                  <th className="px-3 py-2.5 text-right">Outstanding</th>
                  <th className="px-3 py-2.5">Next Due</th>
                  <th className="px-3 py-2.5">Overdue</th>
                </tr>
              </thead>
              <tbody>
                {filteredOutstanding.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Nothing owed. 🎉</td></tr>}
                {filteredOutstanding.map((o) => (
                  <tr key={o.client_id} className="data-row border-t border-border cursor-pointer" onClick={() => nav(`/clients/${o.client_id}`)} data-testid={`out-row-${o.client_id}`}>
                    <td className="px-3 py-2.5 font-semibold">{o.company_name}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{INR(o.contract_value)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-emerald-600 dark:text-emerald-400">{INR(o.received)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-primary font-bold">{INR(o.outstanding)}</td>
                    <td className="px-3 py-2.5 font-mono text-xs">{fmtDate(o.next_due)}</td>
                    <td className="px-3 py-2.5">
                      {o.days_overdue > 0
                        ? <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400 text-xs font-mono"><AlertTriangle size={12} />{o.days_overdue}d</span>
                        : <span className="text-xs text-muted-foreground">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="payments" className="mt-4">
          <div className="flex flex-wrap gap-2 mb-3 items-center">
            <Select value={payClient || "__all__"} onValueChange={(v) => setPayClient(v === "__all__" ? "" : v)}>
              <SelectTrigger className="w-52"><SelectValue placeholder="All clients" /></SelectTrigger>
              <SelectContent><SelectItem value="__all__">All clients</SelectItem>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={payMethod || "__all__"} onValueChange={(v) => setPayMethod(v === "__all__" ? "" : v)}>
              <SelectTrigger data-testid="pay-method-filter" className="w-40"><SelectValue placeholder="Any method" /></SelectTrigger>
              <SelectContent><SelectItem value="__all__">Any method</SelectItem>{METHODS_LIST.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex items-center gap-1"><span className="font-mono text-[10px] uppercase text-muted-foreground">From</span><Input type="date" className="w-40" value={payFrom} onChange={(e) => setPayFrom(e.target.value)} data-testid="pay-from" /></div>
            <div className="flex items-center gap-1"><span className="font-mono text-[10px] uppercase text-muted-foreground">To</span><Input type="date" className="w-40" value={payTo} onChange={(e) => setPayTo(e.target.value)} data-testid="pay-to" /></div>
            <Button variant="outline" size="icon" onClick={load} title="Refresh"><RefreshCw size={14} /></Button>
          </div>
          <div className="border border-border rounded-md overflow-hidden bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  <th className="px-3 py-2.5">Payment</th>
                  <th className="px-3 py-2.5">Client</th>
                  <th className="px-3 py-2.5">Date</th>
                  <th className="px-3 py-2.5">Method</th>
                  <th className="px-3 py-2.5 text-right">Amount</th>
                  <th className="px-3 py-2.5">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayments.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No payments match.</td></tr>}
                {filteredPayments.map((p) => {
                  const client = clients.find((c) => c.id === p.client_id);
                  return (
                    <tr key={p.id} className="data-row border-t border-border cursor-pointer" onClick={() => nav(`/clients/${p.client_id}`)} data-testid={`pay-row-${p.id}`}>
                      <td className="px-3 py-2.5 font-mono text-xs">{p.payment_id}</td>
                      <td className="px-3 py-2.5">{client?.company_name || "—"}</td>
                      <td className="px-3 py-2.5 font-mono text-xs">{fmtDate(p.payment_date)}</td>
                      <td className="px-3 py-2.5 text-xs">{p.method}</td>
                      <td className="px-3 py-2.5 text-right font-mono font-bold">{INR(p.amount)}</td>
                      <td className="px-3 py-2.5 text-xs">
                        {p.status === "void"
                          ? <span className="text-muted-foreground line-through">Void</span>
                          : <span className="text-emerald-600 dark:text-emerald-400 font-mono uppercase text-[10px] tracking-widest">Paid</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
